import ContractDraftContext from '../drafts/ContractDraftContext';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { first } from 'lodash';
import { Subscription } from 'rxjs';
import { Disposer } from '../../custom_shared/misc';
import { NodeBaseModel } from '../models';
import { ChildNodeIds, InputIds, NodeIds } from '../misc';
import { NodeFactory } from '../factories';
import { NodeBaseDto } from '../types';
import { SourceStore } from '../../custom_shared/stores';
import SessionStore from './SessionStore';

export default class NodesStore extends Disposer {
    readonly context = new ContractDraftContext(
        () => this.nodes,
        () => this.sourceStore.sources
    );
    readonly history = this.context.history;
    readonly drafts = this.context.drafts;
    readonly sourceRequests = this.context.sourceRequests;

    activeNodeId: string = '';

    nodes: NodeBaseModel[] = [];

    private renewalSubscriptions: Subscription[] = [];

    constructor(
        private readonly sessionStore: SessionStore,
        private readonly sourceStore: SourceStore
    ) {
        super();

        makeObservable(this, {
            activeNodeId: observable,
            nodes: observable,
            generalInformationNode: computed,
            createOrUpdateNodes: action.bound,
            setActiveNodeId: action.bound
        });

        this.reactions.push(
            reaction(
                () => this.sessionStore.response,
                () => {
                    if (this.sessionStore.inProgress) {
                        return;
                    }

                    const data = this.sessionStore.response?.nodes ?? [];
                    this.drafts.reconcile(this.sessionStore.response?.completedSaveId, () =>
                        this.createOrUpdateNodes(data)
                    );
                }
            )
        );

        this.reactions.push(
            reaction(
                () => this.nodes,
                () => {
                    if (this.activeNodeId && !this.nodes.some(t => t.id === this.activeNodeId)) {
                        this.setActiveNodeId('');
                    }

                    if (!this.activeNodeId && this.firstNode) {
                        this.setActiveNodeId(this.firstNode.id);
                    }
                }
            )
        );
    }

    get firstNode() {
        return first(this.nodes);
    }

    get generalInformationNode() {
        return this.nodes.find(node => node.id === NodeIds.GeneralInformation);
    }

    get limitsNode() {
        return this.nodes.find(node => node.id === NodeIds.Limits);
    }

    createOrUpdateNodes(nodes: NodeBaseDto[]) {
        this.history.withoutCapture(() => {
            this.clearRenewalSubscriptions();
            this.nodes = NodeFactory.createOrUpdateNodes(nodes, this.nodes, this.sourceStore.sources, this.context);
            this.attachRenewalRule();
        });
    }

    setActiveNodeId(activeNodeId: string) {
        this.activeNodeId = activeNodeId;
    }

    getNodeByInputGuid(inputGuid: string) {
        return this.nodes.find(node => node.inputs.some(input => input.guid === inputGuid));
    }

    dispose() {
        this.clearRenewalSubscriptions();
        this.sourceRequests.clear();
        this.disposeReactions();
    }

    private attachRenewalRule() {
        const general = this.generalInformationNode;
        if (!general) {
            return;
        }
        const renewal = general.childNodes.find(node => node.id === ChildNodeIds.GeneralInformation.ContractRenewal);
        const nodes = renewal ? [general, renewal] : [general];
        const createProgram = general.inputs.find(input => input.id === InputIds.GeneralInformation.CreateProgram);
        const resetCreateProgram = () => {
            if (createProgram && createProgram.value !== false) {
                createProgram.setValue(false);
            }
        };
        // Hydration preserves the existing normalization without inventing an edit.
        if (
            nodes.some(node =>
                node.inputs.some(input => input.id === InputIds.GeneralInformation.BusinessId && input.hasValue)
            )
        ) {
            resetCreateProgram();
        }
        this.renewalSubscriptions = nodes.map(node =>
            node.inputValueChangeSubject.subscribe(input => {
                if (input.id === InputIds.GeneralInformation.BusinessId && input.hasValue) {
                    // Subjects run inside the originating edit's cause, including renewal layers.
                    resetCreateProgram();
                }
            })
        );
    }

    private clearRenewalSubscriptions() {
        this.renewalSubscriptions.forEach(subscription => subscription.unsubscribe());
        this.renewalSubscriptions = [];
    }
}
